import * as React from 'react';
import type { GlobUserTemplate } from '../../../MedicareProAPI/Entities/Glob/GlobUserTemplate';
import type { UserLetter } from '../../../MedicareProAPI/Entities/User/UserLetter';
import { UserLetterRepository } from '../../../MedicareProAPI/Repositories/User/UserLetterRepository';
import { UserLetterSharingRepository } from '../../../MedicareProAPI/Repositories/User/UserLetterSharingRepository';
import RichTextEditor from '../Form/Field/RichTextEditor';
import { FileUpload } from '../../Components/Form/Field/FileUpload';
import type { FileUploadResponse } from '../../../MedicareProAPI/API/WebAPI';
import { WebAPI } from '../../../MedicareProAPI/API/WebAPI';
import { UserDocumentRepository } from '../../../MedicareProAPI/Repositories/User/UserDocumentRepository';
import { IndRepository } from '../../../MedicareProAPI/Repositories/Ind/IndRepository';
import { UserDocumentListModal } from '../../Components/UserDocumentsListModal';
import { AskAI } from '../../Components/AskAI';
import { translationOptions } from '../../Constants/AgentReportingTemplates';

interface TemplateEditorProps {
  userId: number;
  accessToken: string;
  template?: UserLetter;
  blankTemplate: boolean;
  starterTemplate?: GlobUserTemplate;
  sharedTemplate?: UserLetter;
  mergeTags: any;
  agencyId: number;
  folderOptions: string[];
  onSave: () => void;
  onAddFolder: (folderName: string) => void;
  onDelete: () => void;
}

type Attachment = {
  systemName: string;
  fileName: string;
};

/**
 * Component for editing a template
 *
 * @component
 * @author Andrew Brown <abrown@medicareprocrm.com>
 * @date Created: 2025-05-20
 * @lastUpdated
 * @version 1.1.0
 */

export const TemplateEditor = ({
  userId,
  accessToken,
  template,
  blankTemplate,
  starterTemplate,
  sharedTemplate,
  mergeTags,
  agencyId,
  folderOptions,
  onAddFolder,
  onSave,
  onDelete,
}: TemplateEditorProps): React.JSX.Element => {
  const userLetterRepository = new UserLetterRepository();
  userLetterRepository.setAccessToken(accessToken);

  const userLetterSharingRepository = new UserLetterSharingRepository();
  userLetterSharingRepository.setAccessToken(accessToken);

  const userDocumentRepository = new UserDocumentRepository();
  userDocumentRepository.setAccessToken(accessToken);

  const individualRepository = new IndRepository();
  individualRepository.setAccessToken(accessToken);

  const webAPI = new WebAPI();
  webAPI.setAccessToken(accessToken);

  const [title, setTitle] = React.useState<string>('');
  const [subject, setSubject] = React.useState<string>('');
  const [type, setType] = React.useState<string>('Letter');
  const [folder, setFolder] = React.useState<string>('');
  const [body, setBody] = React.useState<string>('');
  const [id, setId] = React.useState<number>();
  const [sharing, setSharing] = React.useState<number>();
  const [dirty, setDirty] = React.useState<boolean>(false);
  const [isSaving, setIsSaving] = React.useState<boolean>(false);
  const [attachments, setAttachments] = React.useState<Attachment[]>([]);
  const [dataToggle, setDataToggle] = React.useState<string>('no data');
  const [isTranslating, setIsTranslating] = React.useState<boolean>(false);
  const [isTranslated, setIsTranslated] = React.useState<boolean>(false);
  const editorRef = React.useRef<any>(null);
  const [attachFromDocsPageIsOpen, setAttachFromDocsPageIsOpen] = React.useState<boolean>(false);
  const preTranslationRef = React.useRef<string>('');

  const formDate = Date.now();
  const dataToggleOptions = ['data', 'no data'];

  /**
   * Saves a user letter, either by creating a new one or updating an existing one.
   *
   * @param userId - The ID of the user performing the save
   * @param id - Optional ID of the existing letter (if updating)
   * @param title - The title of the letter
   * @param type - The type/category of the letter (e.g. 'Email', 'SMS')
   * @param folder - Optional folder name where the letter belongs
   * @param body - The body/content of the letter
   * @param sharing - Optional sharing metadata (e.g., shareId or flags)
   * @param attachments - An array of attachments associated with the letter
   */
  const handleSave = async (
    userId: number,
    id: number | null,
    title: string,
    subject: string,
    type: string,
    folder: string | null,
    body: string,
    sharing?: any,
    attachments?: any[]
  ): Promise<void> => {
    if (!title || !type || !body) return;

    setIsSaving(true);

    const payload = {
      letterTitle: title,
      letterSubject: subject,
      letterType: type,
      folder: folder || null,
      letterBody: body,
      attachments: attachments || [],
    };

    try {
      if (id) {
        await userLetterRepository.updateUserLetter(userId, id, payload);
      } else {
        const data = await userLetterRepository.createUserLetter(userId, payload);
        setId(data);
      }

      onSave();
      setIsSaving(false);
      setIsTranslated(false);
    } catch (error) {
      console.error('Failed to save template:', error);
    }
  };

  /**
   * Toggles sharing of a user letter:
   * - If no current sharing exists, creates a new sharing entry
   * - If already shared, deletes the existing sharing entry
   *
   * @param id - The ID of the user letter to be shared or unshared
   * @param sharingId - The ID of the current sharing entry (if it exists)
   */
  const handleTemplateShare = (id: number, sharingId?: number): void => {
    if (!sharing) {
      userLetterSharingRepository
        .createUserLetterSharing(agencyId, id, { agencyId })
        .then((response) => {
          setSharing(response);
        });
    } else {
      userLetterSharingRepository.deleteUserLetterSharing(agencyId, sharingId!).then(() => {
        setSharing(undefined);
      });
    }
  };

  /**
   * Handles a successful file upload by appending it to the attachments list,
   * updating the state, and triggering a save with the new attachment included.
   *
   * @param fileData - The uploaded file response containing metadata
   */
  const handleFileUpload = (fileData: FileUploadResponse): void => {
    const newAttachment = {
      systemName: fileData.name,
      fileName: fileData.fileName,
    };

    const updatedAttachments = [...attachments, newAttachment];
    setAttachments(updatedAttachments);

    handleSave(userId, id, title, subject, type, folder, body, sharing, updatedAttachments);
  };

  /**
   * Removes an attachment from the list by index,
   * updates the state, and triggers a save with the updated list.
   *
   * @param attachmentIndex - The index of the attachment to remove
   */
  const handleRemoveAttachment = (attachmentIndex: number): void => {
    const updatedAttachments = attachments.filter((_, i) => i !== attachmentIndex);
    setAttachments(updatedAttachments);
    handleSave(userId, id, title, subject, type, folder, body, sharing, updatedAttachments);
  };

  /**
   * Converts a legacy or mixed-format attachment string into a consistent array of attachment objects.
   *
   * Handles the following formats:
   * 1. Empty string or null → returns empty array
   * 2. Legacy delimiter format (e.g., "file1*^|file2") → resolves filenames via API
   * 3. JSON-encoded array of attachment objects → parses and returns directly
   * 4. Fallback: single filename string → resolves filename via API
   *
   * @param attachment - The attachment string to convert (can be JSON, delimited string, or single filename)
   * @returns A promise resolving to an array of attachments with `systemName` and `fileName`
   */
  const retroFitAttachments = async (
    attachment: string
  ): Promise<{ systemName: string; fileName: string }[]> => {
    const retrofitted: { systemName: string; fileName: string }[] = [];

    const renderOrClear = (): { systemName: string; fileName: string }[] => {
      return retrofitted.length > 0 ? retrofitted : [];
    };

    // Case 1: null or empty string
    if (!attachment || attachment === '') {
      return renderOrClear();
    }

    // Case 2: old style "filename*^|filename"
    if (attachment.includes('*^|')) {
      const items = attachment.split('*^|');
      for (const item of items) {
        try {
          const response = await userDocumentRepository.getUserDocumentBySystemName(userId, item);
          retrofitted.push({ systemName: item, fileName: response?.docFilename || item });
        } catch (e) {
          console.warn(`Failed to fetch file for ${item}:`, e);
        }
      }
      return renderOrClear();
    }

    // Case 3: valid JSON string
    try {
      const parsed = JSON.parse(attachment);
      if (Array.isArray(parsed)) {
        retrofitted.push(...parsed);
      }
      return renderOrClear();
    } catch (e) {
      // not valid JSON, continue to next fallback
    }

    // Case 4: fallback single system name
    try {
      const response = await userDocumentRepository.getUserDocumentBySystemName(userId, attachment);
      retrofitted.push({ systemName: attachment, fileName: response?.docFilename || attachment });
    } catch (e) {
      console.warn(`Fallback fetch failed for ${attachment}:`, e);
    }

    return renderOrClear();
  };

  /**
   * Translates a template body into the specified language using OpenAI,
   * and updates the state with the translated result.
   *
   * @param body - The original message body to translate
   * @param language - The target language for translation (e.g. "Spanish", "French")
   */
  const handleTranslateTemplate = (body: string, language: string): void => {
    preTranslationRef.current = body;
    setIsTranslating(true);

    webAPI
      .getOpenAIChat({
        chatBody: `Translate this to ${language}, with no additional chat commentary: ${body}`,
      })
      .then((response) => {
        setIsTranslating(false);
        setIsTranslated(true);
        setDataToggle('data');
        setBody(response.choices[0].message.content);
        setDirty(true);
      });
  };

  /**
   * Duplicates a template by resetting the current state to a copy of the provided values,
   * clearing the ID and sharing information to prepare for saving as a new template.
   *
   * @param title - The title of the template being duplicated
   * @param type - The type/category of the template (e.g., "Email")
   * @param folder - The folder where the template is stored
   * @param body - The content/body of the template
   * @param sharing - Sharing metadata (if any) — this is cleared on duplication
   * @param attachments - Array of attachment objects associated with the template
   */
  const handleDuplicate = (
    title: string,
    type: string,
    subject: string,
    folder: string,
    body: string,
    sharing: any,
    attachments: any[]
  ): void => {
    setTitle(`${title} Copy`);
    setSubject(`${subject}`);
    setType(type);
    setFolder(folder);
    setBody(body);
    setId(undefined);
    setSharing(undefined);
    setAttachments(attachments);
    setDirty(true);
  };

  /**
   * Deletes a user letter, and if applicable, removes its associated sharing entry.
   *
   * @param userId - The ID of the user performing the deletion
   * @param id - The ID of the letter to delete
   * @param shareId - Optional ID of the associated sharing entry to delete first
   */
  const handleDelete = async (userId: number, id: number, shareId?: number): Promise<void> => {
    const r = confirm('Are you sure you want to delete this template?');

    if (r) {
      if (shareId !== undefined) {
        await userLetterSharingRepository.deleteUserLetterSharing(userId, shareId);
      }

      await userLetterRepository.deleteUserLetter(userId, id);
      onDelete();
    }
  };

  /**
   * Reverts the translated template body back to its original state
   * using the value stored in `preTranslationRef`.
   */
  const handleUndoTranslation = (): void => {
    setBody(preTranslationRef.current);
    setIsTranslated(false);
  };

  /**
   * Handles attachments added from the "Attach from Docs" page,
   * closes the modal, updates the attachments list, and saves the updated template.
   *
   * @param fileData - An array of new attachments to append
   */
  const handleAttachFromDocsPageSave = (fileData: Attachment[]): void => {
    setAttachFromDocsPageIsOpen(false);

    const updatedAttachments = [...attachments, ...fileData];
    setAttachments(updatedAttachments);

    handleSave(userId, id, title, subject, type, folder, body, sharing, updatedAttachments);
  };

  /**
   * Updates the template body and marks the form as dirty (modified).
   *
   * @param body - The new body content of the template
   */
  const handleBodyChange = (body: string): void => {
    setBody(body);
    setDirty(true);
  };

  /**
   * Confirms the AI-generated content by setting it as the template body
   * and marking the form as dirty (modified).
   *
   * @param data - The AI-generated body content to use
   */
  const handleAiConfirm = (data: string): void => {
    console.log(data);
    setDirty(true);
    setBody(data);
  };

  React.useEffect(() => {
    const loadFrom = async (source, isNew) => {
      if (!source) return;

      setTitle(source.getLetterTitle?.() || source.getTemplateTitle?.());
      setSubject(source.getLetterSubject?.() || source.getTemplateTitle?.());
      setType(source.getLetterType?.() || source.getTemplateType?.() || 'Email');
      setFolder(source.getFolder?.() || source.getCategory?.() || '');
      setBody(source.getLetterBody?.() || source.getTemplateBody?.() || '');
      setId(isNew ? null : source.getLetterId?.() || null);
      setDirty(isNew);

      if (source.getSharing) {
        setSharing(source.getSharing()?.shareId);
      }

      if (source.getAttachments) {
        const result = await retroFitAttachments(source.getAttachments());
        setAttachments(result);
      }

      onAddFolder(source.getFolder?.() || source.getCategory?.() || '');
    };

    if (template) {
      loadFrom(template, false);
    } else if (starterTemplate) {
      loadFrom(starterTemplate, true);
    } else if (sharedTemplate) {
      loadFrom(sharedTemplate, true);
    } else if (blankTemplate) {
      setTitle('');
      setSubject('');
      setType('Email');
      setFolder('');
      setBody('');
      setId(null);
      setDirty(false);
    }
  }, [template, starterTemplate, blankTemplate, sharedTemplate]);

  return (
    <>
      <div className="row">
        <div className="col-12">
          <h6>
            <b>Editor</b>
          </h6>
        </div>

        {(template || starterTemplate || blankTemplate || sharedTemplate) && (
          <>
            {/* Name */}
            <div className="col-8 col-md-6 mb-2">
              <label className="label">Template Name</label>
              <input
                type="text"
                className="form-input form-control form-control-sm"
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  setDirty(true);
                }}
              />
            </div>

            {/* Type */}
            <div className="col-4 col-md-3 mb-2">
              <label className="label">Type</label>
              <select
                className="form-control form-control-sm"
                value={type}
                onChange={(e) => {
                  setType(e.target.value);
                  setDirty(true);
                }}
              >
                <option value="Letter">Letter</option>
                <option value="Email">Email</option>
                <option value="SMS">SMS</option>
              </select>
            </div>

            {/* Folder */}
            <div className="col-4 col-md-3 mb-2">
              <label className="label">Category</label>
              <select
                className="form-control form-control-sm"
                value={folder}
                onChange={(e) => {
                  const selected = e.target.value;

                  if (selected === 'addNew') {
                    const newFolder = prompt('Enter a new folder name:');
                    if (newFolder && newFolder.trim() !== '') {
                      const trimmed = newFolder.trim();
                      setFolder(trimmed);
                      onAddFolder(trimmed);
                    }
                  } else {
                    setFolder(selected);
                  }

                  setDirty(true);
                }}
              >
                <option value="">Select...</option>
                {folderOptions.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
                <option value="addNew">ADD NEW</option>
              </select>
            </div>

            {type === 'Email' && (
              <div className="col-12">
                <label className="label">Template Subject</label>
                <input
                  type="text"
                  className="form-input form-control form-control-sm"
                  value={subject}
                  onChange={(e) => {
                    setSubject(e.target.value);
                    setDirty(true);
                  }}
                />
              </div>
            )}

            {/* Share with downline */}
            {agencyId > 0 && id && (
              <div className="col-12 shareCol">
                <div>
                  <label className="label">Share with downlines</label>
                </div>
                <label
                  className="switch"
                  data-toggle="tooltip"
                  title="share template with downlines"
                >
                  <input
                    type="checkbox"
                    checked={sharing > 0}
                    id="sharing"
                    className="letterShareToggle"
                    data-status=""
                    data-share=""
                    data-id=""
                    onChange={() => handleTemplateShare(id, sharing)}
                  />
                  <span className="slider round"></span>
                </label>
              </div>
            )}

            {/* Save/update */}
            <div className="col-12 mt-3 saveRow">
              <button
                onClick={() =>
                  handleSave(userId, id, title, subject, type, folder, body, sharing, attachments)
                }
                className="btn btn-sm btn-primary"
                disabled={!dirty || !title || !type}
              >
                {isSaving ? (
                  <span>
                    <i className="far fa-spinner fa-spin mr-2"></i>
                    Updating...
                  </span>
                ) : id > 0 ? (
                  'Update'
                ) : (
                  'Save'
                )}
              </button>
            </div>

            {/* SMS disclosure */}
            {type === 'SMS' && (
              <div className="col-12 mt-3 smsInfo">
                <div className="alert alert-warning p-2" style={{ fontSize: '9pt' }}>
                  SMS templates won't adhere to any styling or images. Plain text and merge tags
                  only.
                </div>
              </div>
            )}

            {/* Translate / Preview live data */}
            <div className=" col-12 mt-2 text-right">
              {/*
                    dataToggleOptions.map(type => {
                        let dataToggleClassList = ["badge", "border", "border-secondary", "mr-1", "cp"];

                        if (dataToggle === type) {
                            dataToggleClassList.push('badge-secondary');
                          } else {
                            dataToggleClassList.push('calMess');
                          }

                        return  <div className={dataToggleClassList.join(' ')} onClick={e => setDataToggle(type)}>{type}</div>
                    })
                */}
              <div className="dropdown d-inline-block">
                <div className="badge badge-info mr-2 dropdown-toggle" data-toggle="dropdown">
                  {isTranslating ? (
                    <>
                      <i className="far fa-spinner fa-spin"></i> Translating...
                    </>
                  ) : (
                    <>Translate</>
                  )}
                </div>
                <div className="dropdown-menu">
                  {translationOptions.map((item, index) => {
                    return (
                      <div
                        key={'translate' + index}
                        className="dropdown-item cp mx-2 px-1 text-dark"
                        onClick={(e) => handleTranslateTemplate(body, item)}
                      >
                        {item}
                      </div>
                    );
                  })}
                </div>
              </div>
              {isTranslated && (
                <div
                  className="badge badge-secondary mr-2 dropdown-toggle"
                  onClick={handleUndoTranslation}
                >
                  Undo Translation
                </div>
              )}
              {id > 0 && (
                <div
                  className="badge badge-secondary mr-2 dropdown-toggle"
                  onClick={() =>
                    handleDuplicate(title, type, subject, folder, body, sharing, attachments)
                  }
                >
                  Duplicate
                </div>
              )}
            </div>

            {/* Editor */}
            <div className="col-12 mt-3">
              <RichTextEditor
                value={body}
                mergeTags={mergeTags}
                menuBar={true}
                onEditorChange={(e) => handleBodyChange(e)}
              />
            </div>
            <div className="col-12">
              <AskAI accessToken={accessToken} onConfirm={handleAiConfirm} />
            </div>

            {/* attachments */}

            <div className="col-9 mt-1 attachmentRow">
              {type === 'Email' && (
                <div className="dropdown" style={{ display: 'inline-block' }}>
                  <div className="pt-3">
                    <div className="calMess attachTitle">
                      <b className="mb-2 mr-2">Attachments</b>

                      <FileUpload
                        bucket="medicareprocrm"
                        path={`documents`}
                        extensions={['pdf', 'jpeg', 'png']}
                        autoUpload={true}
                        onChange={handleFileUpload}
                        reusable={true}
                        fileUploadButtonLabel={'Upload from device'}
                        selectButtonColor={'primary'}
                        uploadButtonColor={'primary'}
                        buttonStyle={'badge'}
                        inline={true}
                      />
                      <div
                        className="badge badge-success cp mr-2 mb-2"
                        style={{ fontSize: '8pt' }}
                        onClick={() => setAttachFromDocsPageIsOpen(true)}
                      >
                        <i className="far fa-plus-circle"></i> Attach from Docs page
                      </div>
                    </div>
                    <div>
                      {Array.isArray(attachments) &&
                        attachments.length > 0 &&
                        attachments.map((item, itemIndex) => {
                          return (
                            <div
                              key={'attachment' + itemIndex}
                              className="calMess fileInstance p-2 mb-2 rounded font-weight-bold"
                              style={{ border: '2px dotted #999999', fontSize: '10pt' }}
                            >
                              <i className="far fa-file-import mr-2 "></i>
                              <span>{item.fileName}</span>
                              <span
                                className="badge badge-secondary badge-pill ml-2 cp"
                                style={{ fontSize: '8pt' }}
                                onClick={() => handleRemoveAttachment(itemIndex)}
                              >
                                <i className="far fa-times"></i> Remove
                              </span>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Delete */}
            {id > 0 && (
              <div className="col-3 mt-3 delRow text-right">
                <i
                  className="fas fa-trash text-danger deleteTemplate float-right"
                  onClick={() => handleDelete(userId, id, sharing)}
                ></i>
              </div>
            )}
          </>
        )}
      </div>
      {attachFromDocsPageIsOpen && (
        <UserDocumentListModal
          userId={userId}
          userAccessToken={accessToken}
          open={attachFromDocsPageIsOpen}
          onClose={() => setAttachFromDocsPageIsOpen(false)}
          onSave={handleAttachFromDocsPageSave}
        />
      )}
    </>
  );
};
